import { ref, computed } from 'vue'
import { TenantPackagesApi, type ActivePackageDto } from '../services/packages'

const cache = ref<ActivePackageDto | null>(null)
const stamp = ref(0)
const lastFetchedAt = ref<number>(0)
let inFlight: Promise<ActivePackageDto | null> | null = null

const DEFAULT_TTL = 1440 * 60 * 1000

type EnsureOpts = {
  force?: boolean
  ttlMs?: number
}

export async function ensureActivePackage(opts: EnsureOpts = {}) {
  const ttl = opts.ttlMs ?? DEFAULT_TTL
  const now = Date.now()

  const fresh = cache.value && now - lastFetchedAt.value < ttl
  if (cache.value && !opts.force && fresh) {
    return cache.value
  }

  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const { data } = await TenantPackagesApi.active()
      cache.value = data
      lastFetchedAt.value = Date.now()
      stamp.value++
      return cache.value
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

export function resetActivePackage() {
  cache.value = null
  lastFetchedAt.value = 0
  stamp.value++
}

export const activePackage = computed(() => cache.value)
export const pkgStamp = computed(() => stamp.value)

const MENU_PREFIX_MAP: Record<string, string> = {
  menus: 'menu.',
  categories: 'category.',
  products: 'product.',
  options: 'option.',
  tables: 'table.',
  tickets: 'ticket.',
  campaigncodes: 'campaigncodes.',
  roles: 'role.',
  users: 'user.',
  stocks: 'stock.',
  dashboard: 'dashboard.',
  analytics: 'analytic',
  feedbacks: 'feedback.',
}

export function canSee(menuKey: string) {
  const p = cache.value
  if (!p) return false

  if (p.permissions?.some((x) => x.menuKey === menuKey)) return true

  const prefix = MENU_PREFIX_MAP[menuKey]
  if (prefix && p.apiPermissions?.some((x) => x.key.startsWith(prefix))) return true

  return false
}

export function useCanSee(menuKey: string) {
  return computed(() => canSee(menuKey))
}

export function getLimit(menuKey: string): number | null {
  const rec = cache.value?.permissions?.find((x) => x.menuKey === menuKey)
  return rec?.recordLimit ?? null
}
